import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyFlags, LaunchDarklyRoomArtifacts } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import {
  ModalDeleteSocialComponent,
  ModalDeleteSocialData,
} from 'app/modules/social/modal-delete-social/modal-delete-social.component';
import { socialHomePageActions } from 'app/modules/social/state/home-page';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import SocialRoomSet from 'entity/SocialRoomSet';
import SocialTemplate from 'entity/SocialTemplate';
import SocialTheme from 'entity/SocialTheme';
import SocialController from 'rest/SocialController';

import { scootaversePageActions, scootaversePageSelectors } from '../state';
import { SCOOTAVERSE_PAGE_SIZE } from '../state/reducer';

@Injectable({
  providedIn: 'root',
})
export class ScootaversePageService extends Loggable implements OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);

  private page = 0;
  private pageSize = SCOOTAVERSE_PAGE_SIZE;
  private hasMore = true;

  public isLoading = false;

  /*Create room variables*/
  public isCompanyAdmin = false;
  public isPaidUser = false;
  public ldRoomArtifacts?: LaunchDarklyRoomArtifacts;
  public templates: SocialTemplate[] = [];
  private artifactId?: string = '';

  protected roomName?: string;
  protected scootaverseName?: string;
  protected socialId?: string;
  protected scootaverseId?: string;

  public selectedScootaverse$: BehaviorSubject<SocialRoomSet | undefined> = new BehaviorSubject<
    SocialRoomSet | undefined
  >({});

  private socialController = new SocialController();

  public constructor(
    private store: Store,
    private router: Router,
    private dialog: MatDialog,
    private _toasterService: ToastrService,
  ) {
    super();
    this.setDisplayName('ScootaversePageService');

    this.store
      .select(scootaversePageSelectors.getScootaverses)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ hasMore, page, size }) => {
        this.page = page;
        this.pageSize = size;
        this.hasMore = hasMore;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_room_artifact_set)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((ldRoomArtifacts) => (this.ldRoomArtifacts = ldRoomArtifacts));

    this.store.dispatch(scootaversePageActions.INIT());
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public set selectedScootaverse(scootaverse: SocialRoomSet) {
    this.selectedScootaverse$.next(scootaverse);
  }

  //Update selected Scootaverse variable to trigger updates in room page and others.
  //* replaceScootaverseId -- Changes the selected scootaverse to a new one. Omit this parameter if you want to update the actual scootaverse information.
  //* clear -- Clear the selected scootaverse
  public updateSelectedScootaverse(replaceScootaverseId?: string, clear?: boolean): void {
    if (!clear) {
      const toUpdateScootaverseId = replaceScootaverseId ?? this.scootaverseId;
      if (toUpdateScootaverseId) {
        this.socialController
          .getSocialRoomSet(toUpdateScootaverseId)
          .then((scootaverse) => {
            this.selectedScootaverse = scootaverse;
            this.scootaverseId = toUpdateScootaverseId;
            return;
          })
          .catch((err: unknown) => {
            this.error('Error loading scootaverse information: ', err);
            void this.redirectToHome();
          });
      }
    } else {
      this.selectedScootaverse$.next(undefined);
      this.scootaverseId = undefined;
    }
  }

  /**
   * Fetch the first page of scootaverses
   */
  public fetchScootaverses(): void {
    this.store.dispatch(scootaversePageActions.FETCH_SCOOTAVERSES_REQ({ page: 0, size: this.pageSize }));
  }

  /**
   * Fetch the first page of rooms
   */
  public fetchRooms(replaceScootaverseId?: string): void {
    //Entered a new scootaverse, send and update the scootaverse information
    if (replaceScootaverseId) {
      this.updateSelectedScootaverse(replaceScootaverseId);
      this.store.dispatch(scootaversePageActions.SET_SELECTED_SCOOTAVERSE({ scootaverseId: replaceScootaverseId }));
    } else {
      //Re-fetch the same scootaverse information
      this.updateSelectedScootaverse();
      this.store.dispatch(scootaversePageActions.SET_SELECTED_SCOOTAVERSE({ scootaverseId: this.scootaverseId || '' }));
    }

    this.store.dispatch(socialHomePageActions.CLEAR_DELETE_RSP());
  }

  /**
   * Fetch the next page of scootaverses
   */
  public fetchMoreScootaverses(): void {
    if (!this.hasMore) {
      return;
    }
    this.store.dispatch(scootaversePageActions.FETCH_SCOOTAVERSES_REQ({ page: (this.page += 1), size: this.pageSize }));
  }

  /**
   * Redirect to main page
   */
  public async redirectToHome(): Promise<void> {
    this.updateSelectedScootaverse(undefined, true);
    await this.router.navigateByUrl(`home/scootaverses`);
  }

  /**
   * Redirect to scootaverse page
   */
  public async onEnterScootaverse(roomSetId: string, newTab?: boolean): Promise<void> {
    assertDefined(roomSetId);
    this.scootaverseId = roomSetId;
    if (!newTab) {
      await this.router.navigate([`home/scootaverses/${roomSetId}/rooms`]);
    } else {
      const newRelativeUrl = this.router.createUrlTree([`home/scootaverses/${roomSetId}/rooms`]);
      const baseUrl = window.location.href.replace(this.router.url, '');
      open(baseUrl + newRelativeUrl, '_blank');
    }
  }

  /**
   * Redirect to room edit page
   */
  public async onEditRoom(room?: Social, scootaverseId?: string): Promise<void> {
    if (room && scootaverseId) {
      const { socialId } = room;
      assertDefined(socialId);

      await this.router.navigate([`home/scootaverses/${this.scootaverseId}/room-editor/${socialId}`]);
    }
  }

  /**
   * Redirect to room advanced settings page
   */
  public async onAdvanceSettingsRoom(room?: Social, editMode?: boolean): Promise<void> {
    if (room) {
      const { socialId } = room;
      assertDefined(socialId);
      const queryParams = editMode ? { 'edit-mode': editMode?.toString() } : {};
      await this.router.navigate([`home/scootaverses/${this.scootaverseId}/room/${socialId}/advanced-settings`], {
        queryParams,
      });
    }
  }

  public async onCreateScootaverse(createdScootaverse: SocialRoomSet): Promise<void> {
    if (createdScootaverse) {
      await this.router.navigate([`home/scootaverses/${createdScootaverse.socialRoomSetId}/rooms`]);
    }
  }

  /**
   * Redirect to create room page
   */
  public async onCreateRoom(scootaverseId?: string): Promise<void> {
    if (scootaverseId) {
      assertDefined(scootaverseId);
      await this.router.navigate([`home/scootaverses/${scootaverseId}/create-room`]);
    }
  }

  /**
   * Redirect to settings room page
   */
  public async onScootaverseSettings(scootaverseId?: string, newTab?: boolean): Promise<void> {
    assertDefined(scootaverseId);
    this.scootaverseId = scootaverseId;
    if (!newTab) {
      await this.router.navigate([`home/scootaverses/${scootaverseId}/settings`]);
    } else {
      const newRelativeUrl = this.router.createUrlTree([`home/scootaverses/${scootaverseId}/settings`]);
      const baseUrl = window.location.href.replace(this.router.url, '');
      open(baseUrl + newRelativeUrl, '_blank');
    }
  }

  /**
   *Delete scootaverse confirmation and action
   */
  public onDeleteScootaverse(scootaverse: SocialRoomSet): void {
    if (scootaverse.socialRoomSetId) {
      const data = {
        modalTitle: 'Delete',
        text: 'Are you sure you want to delete this Scootaverse?',
        primaryButtonText: 'Okay',
        secondaryButtonText: 'Cancel',
      };

      const dialogRef = this.dialog.open<ModalDeleteSocialComponent, ModalDeleteSocialData>(
        ModalDeleteSocialComponent,
        {
          width: '540px',
          panelClass: 'no-padding-social-page-modal',
          backdropClass: 'social-page-backdrop',
          data: data,
        },
      );

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((result: boolean) => {
          if (result && scootaverse.socialRoomSetId) {
            this.socialController
              .deleteSocialRoomSet(scootaverse.socialRoomSetId)
              .then(() => {
                this._toasterService.success('Scootaverse deleted successfully!', 'Success');
                this.store.dispatch(scootaversePageActions.FETCH_SCOOTAVERSES_REQ({ page: 0, size: this.pageSize }));
              })
              .catch((e) => {
                this._toasterService.error('Error deleting scootaverse. Try again later', 'Error');
                this.error(e);
              });
          }
        });
    }
  }

  public copySocial(room: Social) {
    return this.socialController.copySocial(room.socialId || '', `Copy of ${room?.name}`);
  }

  public copySocialRoomSet(roomSet: SocialRoomSet) {
    return this.socialController.copySocialRoomSet(roomSet.socialRoomSetId || '', `Copy of ${roomSet?.name}`);
  }

  public async updateSocialRoomSet(socialRoomSet: SocialRoomSet) {
    return await this.socialController.updateSocialRoomSet(socialRoomSet);
  }

  private sortSocials(socials: Social[]) {
    const orderedSocials = socials.sort((a, b) => {
      const dateA = a.dateCreated?.getTime();
      const dateB = b.dateCreated?.getTime();

      if (dateA && dateB) {
        if (isNaN(dateA) && isNaN(dateB)) {
          return 0;
        } else if (isNaN(dateA)) {
          return 1;
        } else if (isNaN(dateB)) {
          return -1;
        } else {
          return dateB - dateA;
        }
      }
      return 0;
    });
    return orderedSocials;
  }

  public getSocialTheme(roomSet?: SocialRoomSet): SocialTheme | undefined {
    if (roomSet?.socials) {
      const orderedSocials = this.sortSocials([...roomSet.socials]);
      const validSocial = orderedSocials.find((social) => social.isValid); // Find first valid room set inside the ordered array
      return validSocial ? validSocial.theme : undefined;
    }
    return undefined;
  }

  /**ENTRY FLOW PAGES */

  public async redirectToEntryFlowList(scootaverseId?: string): Promise<void> {
    if (scootaverseId) {
      assertDefined(scootaverseId);
      await this.router.navigate([`home/scootaverses/${scootaverseId}/entry-flows`]);
    }
  }

  /**
   * Redirect to create entry flow page
   */
  public async onCreateEntryFlow(scootaverseId?: string): Promise<void> {
    if (scootaverseId) {
      assertDefined(scootaverseId);
      await this.router.navigate([`home/scootaverses/${scootaverseId}/entry-flows/create-flow`]);
    }
  }

  /**
   * Redirect to edit EntryFlow
   */
  public async onEditEntryFlow(scootaverseId?: string, configurationId?: string): Promise<void> {
    if (scootaverseId) {
      assertDefined(scootaverseId);
      await this.router.navigate([`home/scootaverses/${scootaverseId}/entry-flows/edit-flow/${configurationId}`]);
    }
  }

  public async editRoomStep(roomId?: string, scootaverseId?: string, step?: string): Promise<void> {
    if (roomId && scootaverseId && step) {
      await this.router.navigate([`home/scootaverses/${scootaverseId}/room-editor/${roomId}/step/${step}`]);
    }
  }
}
